/* eslint-disable @typescript-eslint/no-explicit-any */
import { queryClient } from '@oneview/queries';
import { isProduction } from '@oneview/utils/branchUtils';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { History } from 'history';
import React, { PropsWithChildren, FC } from 'react';
import { Provider } from 'react-redux';
import { bindActionCreators, Store } from 'redux';
import { HistoryRouter as Router } from 'redux-first-history/rr6';
import { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import * as utils from '../utils';
import { App } from './App';
import { ThemeProvider } from './theme-provider';
import { useHydrateAtoms } from 'jotai/react/utils';
import { queryClientAtom } from 'jotai-tanstack-query';

const HydrateAtoms: FC<PropsWithChildren> = ({ children }) => {
    useHydrateAtoms([[queryClientAtom, queryClient]]);
    return children;
};

interface Props {
    store: Store;
    history: History;
    persistor: Persistor;
}

export class Root extends React.Component<Props> {
    componentDidMount() {
        // Expose some data for development
        if (!isProduction()) {
            const { store } = this.props;
            const configurable = true;
            let exposedApi: any;

            // Retrieve API object using thunk action
            bindActionCreators(
                () => (_: any, __: any, api: any) => {
                    exposedApi = api;
                },
                store.dispatch,
            )();

            Object.defineProperties(window, {
                api: { value: exposedApi, configurable },
                store: { value: store, configurable },
                utils: { value: utils, configurable },
                state: { get: () => store.getState(), configurable },
            });
        }
    }

    render() {
        const { store, history, persistor } = this.props;

        return (
            <QueryClientProvider client={queryClient}>
                <Provider store={store}>
                    <Router history={history}>
                        <PersistGate persistor={persistor as any}>
                            <ThemeProvider>
                                <HydrateAtoms>
                                    <App />
                                </HydrateAtoms>
                            </ThemeProvider>
                        </PersistGate>
                    </Router>
                </Provider>
                <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
        );
    }
}
